import React, { useMemo, useState } from "react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { X } from "lucide-react";
import IpfsImage from "./IpfsImage";
import { IPFS_GATEWAY } from "../lib/media";

const VIDEO_GATEWAY_HOSTS = [IPFS_GATEWAY];

const extractCid = (url) => {
    if (!url) return null;
    // Match both http(s)://gw/ipfs/CID and schemeless gw/ipfs/CID, plus subdomain form CID.ipfs.*
    const m = url.match(/(?:^|\/)ipfs\/(?:ipfs\/)*([a-zA-Z0-9]+)(?:\/.*)?$/) || url.match(/^(?:https?:\/\/)?([a-z0-9]+)\.ipfs\./i);
    return m ? m[1] : null;
};

const buildVideoSources = (url) => {
    if (!url) return [];
    const cid = extractCid(url);
    if (!cid) {
        const safeUrl = url.startsWith("http") ? url : `https://${url}`;
        return [safeUrl];
    }
    const list = [];
    for (const h of VIDEO_GATEWAY_HOSTS) {
        list.push(`${h}/ipfs/${cid}`);
    }
    return list;
};

const MediaModal = ({ media, onClose }) => {
    const sources = useMemo(() => media?.type === "video" ? buildVideoSources(media.url) : [], [media]);
    const [srcIdx, setSrcIdx] = useState(0);

    if (!media) return null;
    const currentVideoSrc = sources[srcIdx] || media.url;

    return (
        <Dialog open={!!media} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-3xl p-0 overflow-hidden border-[#E5DDCB] bg-[#1C1C1A]"
                data-testid="media-modal"
            >
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-sm text-white"
                    data-testid="media-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>
                {media.type === "image" ? (
                    <IpfsImage src={media.url} alt={media.name} className="w-full h-auto max-h-[80vh] object-contain" />
                ) : (
                    <video
                        // key resets element when gateway changes → force re-fetch
                        key={`${media.url}-${srcIdx}`}
                        src={currentVideoSrc}
                        controls
                        playsInline
                        muted
                        preload="auto"
                        onLoadedMetadata={(e) => {
                            const p = e.currentTarget.play();
                            if (p && typeof p.catch === "function") p.catch(() => {});
                        }}
                        onError={() => {
                            if (srcIdx < sources.length - 1) {
                                console.warn("[video] failed on", currentVideoSrc, "→ trying next gateway");
                                setSrcIdx(srcIdx + 1);
                            }
                        }}
                        className="w-full h-auto max-h-[80vh] bg-black"
                        data-testid="media-modal-video"
                    />
                )}
                <div className="p-4 bg-[#1C1C1A] text-white">
                    <div className="font-serif-display text-xl">{media.name}</div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default MediaModal;
